import type { SocialLink, Text } from '@kkf/design'
import { POSTS } from './blog'
import type { MessageKey } from '../i18n'
import { appUrl } from '@kkf/design'
import { LINKS } from './links'

/**
 * Content for the garage's shell. The header, footer, sidebar and search modal are the
 * design system's components — this file is only what goes in them.
 *
 * Imagery is referenced from the web root (`/assets/img/...`), filled by
 * `tools/sync-assets.mjs` on `predev`/`prebuild`. A relative path would break on any
 * nested route.
 */

/**
 * This app's lockup: the Foundation's lion and "KING OF KINGS" over "GARAGE".
 *
 * Every app had `logo.png` until 2026-09-19 — the FOUNDATION's artwork, strapline and all —
 * and set its venture's name as a separate word beneath it, so the mark named the charity
 * and the chrome named the venture. There is now one lockup per app, generated from a
 * single construction by `tools/logo/build-lockups.py`; see that script and
 * `tools/logo/reference/README.md`.
 *
 * `LOGO` is for pale chrome, which is every surface this app has. `LOGO_WHITE` is the same
 * mark for a dark ground.
 */
export const LOGO = '/assets/img/logo-garage.svg'
export const LOGO_WHITE = '/assets/img/logo-garage-white.svg'

/**
 * The WIDE lockup — the lion beside the type rather than above it — for the header and the
 * off-canvas, which are short and wide.
 *
 * The stacked mark puts sixty per cent of its height into the lion, and the header bar is
 * clamp(76px, 4.52vw, 86px) tall: at the 74px `.logo` allows, its venture line renders
 * 2.7px, which is not small but absent. The wide one spends the bar's width instead and the
 * same word comes out near 7px. The footer and the 404 keep the stacked file — they have
 * vertical room and no width to spare.
 */
export const LOGO_WIDE = '/assets/img/logo-garage-wide.svg'
export const LOGO_WIDE_WHITE = '/assets/img/logo-garage-wide-white.svg'

/** One Facebook page for the whole Foundation, the garage included. */
/**
 * The blog column in the footer.
 *
 * **Derived from the posts this site carries, since 2026-09-21.** The blog used to be the
 * Foundation's alone, so this column carried two hand-written invitations instead — the
 * comment that said so is below, and the invitations are still the fallback, because a
 * venture nobody has written about yet has nothing to list and an empty column reads as a
 * column that failed to load.
 *
 * Only dated posts, newest first, two of them: the column prints a date beside each title
 * and an evergreen post has none.
 */
export const FOOTER_POSTS = POSTS.filter((post) => post.published)
  .slice(0, 2)
  .map((post) => ({
    image: post.image,
    date: post.published ?? '',
    title: post.title,
    to: `/blog/${post.slug}`,
  }))

export const SOCIALS: SocialLink[] = [
  { icon: 'facebook', href: 'https://www.facebook.com/profile.php?id=61592414898013', label: 'Facebook' },
]

/**
 * Four entries, and **no Home.**
 *
 * The logo is how you get home — which is the convention on every site this app's register
 * is drawn from, and it buys back the width that a fifth entry was spending to duplicate
 * something already in the bar. `LOGO` is wrapped in a link to `homeTo` by `Header`, so
 * the affordance is there whether or not a nav entry names it.
 *
 * `Contact` is not in the bar. It is reachable from the footer, from the contact page's own
 * form and from the recovery band's phone number — a visitor who wants to reach the
 * workshop has louder routes than a nav entry.
 *
 * **`Partner Products` leaves this site (2026-09-19).** It was `Products`, a `NavMega` panel
 * of cards built from the two products the Garage sold through `/shop`. The shop, the
 * products and the cart are gone — the gear is Radical RZ's and is sold on their store — so
 * the entry is a plain `href`, and `HeaderNav` opens every `href` entry in a new tab, which
 * is the right behaviour for a link that leaves the site entirely.
 *
 * The three sibling apps are **not** here — they have been footer-only since 2026-09-02,
 * because six entries made the header a site map of the whole group and pushed the
 * garage's own pages out of a visitor's eyeline.
 */
export const NAV: { labelKey: MessageKey; to?: string; href?: string }[] = [
  { labelKey: 'nav.partnerProducts', href: 'https://radicalrz.com/shop' },
  { labelKey: 'nav.tours', to: '/tours' },
  { labelKey: 'nav.services', to: '/services' },
  { labelKey: 'nav.programmes', to: '/programmes' },
]

/**
 * The header's one button is the one thing a visitor to this app is here to do.
 *
 * It used to say Donate and leave for the Foundation. That was wrong twice over: the garage
 * takes no donations, and a visitor who has just read about a trail does not want a
 * charity's payment page — they want to book the trail. Donating is the Foundation's job
 * and its own header still says so; the footer's wayfinding row is how you get there from
 * here.
 */
export const HEADER_CTA = { labelKey: 'header.cta' as MessageKey, to: '/tours' }

export const FOOTER = {
  preamble: {
    subTitle: 'King of Kings Garage',
    title: {
      en: 'A Workshop That Pays For A Youth Programme',
      mn: 'Залуучуудын хөтөлбөрийн зардлыг төлдөг цех',
    } satisfies Text,
    paragraphs: [
      {
        en: 'Recovery, servicing and trail support for 4x4s and off-road bikes, on one of Mongolia’s great off-road routes. That is a real business at a real price, and the margin on it is what keeps the training free.',
        mn: 'Монголын нэрт замын бус замуудын нэг дээр 4x4 болон замын бус мотоциклд зориулсан чирэх, засвар, замын дэмжлэг. Энэ бол жинхэнэ үнэтэй жинхэнэ бизнес бөгөөд түүнээс гарах ашиг нь сургалтыг үнэгүй байлгадаг.',
      },
      {
        en: 'In the same Garage, young people learn a trade on real jobs beside mentors who keep turning up. Nothing on the programme side costs a young person anything, ever.',
        mn: 'Мөн тэр Гаражид залуучууд байнга ирсээр байдаг зөвлөхүүдийн хажууд жинхэнэ ажил дээр мэргэжил сурдаг. Хөтөлбөрийн талд залуу хүнээс хэзээ ч ямар ч төлбөр авдаггүй.',
      },
    ] satisfies Text[],
    image: '/assets/img/garage-workshop-lift.jpg',
    imageAlt: {
      en: 'A vehicle raised on a workshop lift, tools and a bench behind it',
      mn: 'Цехийн өргөгч дээр өргөгдсөн тээврийн хэрэгсэл, ард нь багаж, ажлын ширээ',
    } satisfies Text,
  },
  wayfinding: {
    labelKey: 'footer.wayfinding' as MessageKey,
    links: [
      { labelKey: 'nav.services' as MessageKey, to: '/services' },
      { labelKey: 'nav.programmes' as MessageKey, to: '/programmes' },
      { labelKey: 'nav.foundation' as MessageKey, href: LINKS.foundation },
      { labelKey: 'nav.resort' as MessageKey, href: LINKS.resort },
      { labelKey: 'nav.partners' as MessageKey, href: LINKS.partners },
    ],
  },
  logo: LOGO,
  homeTo: '/',
  about: {
    en: 'King of Kings Garage is the Foundation’s workshop: off-road recovery and servicing, and a training space where young people learn a trade, a skill and their own worth.',
    mn: 'King of Kings Garage бол Сангийн цех юм: замын бус чирэх, засвар үйлчилгээ, мөн залуучууд мэргэжил, ур чадвар, өөрсдийн үнэ цэнийг сурдаг сургалтын орон зай.',
  } satisfies Text,
  socials: SOCIALS,
  quickLinks: {
    titleKey: 'footer.quickLinks' as MessageKey,
    links: [
      { labelKey: 'nav.home' as MessageKey, to: '/' },
      { labelKey: 'nav.services' as MessageKey, to: '/services' },
      { labelKey: 'nav.programmes' as MessageKey, to: '/programmes' },
      { labelKey: 'nav.contact' as MessageKey, to: '/contact' },
      { labelKey: 'nav.blog' as MessageKey, to: '/blog' },
      { labelKey: 'nav.donate' as MessageKey, href: appUrl(LINKS, 'foundation', '/donations') },
    ],
  },
  /**
   * The workshop's own posts, when there are any — see `FOOTER_POSTS`. The two invitations
   * below are the fallback, and were the whole column while the Foundation owned the blog:
   * they are what a reader at the bottom of a garage page is actually deciding between.
   */
  recentPosts: {
    title: { en: 'Two Ways In', mn: 'Орох хоёр зам' } satisfies Text,
    posts: [
      {
        image: '/assets/img/garage-riders-camp.jpg',
        // `date` here is not a date — it is the line the recent-posts slot prints in that
        // position, and it is copy.
        date: { en: 'Nothing to pay, ever', mn: 'Хэзээ ч төлөх зүйлгүй' } satisfies Text,
        title: {
          en: 'Just come in and stand in the workshop',
          mn: 'Зүгээр л орж ирээд цехэд зогсоорой',
        } satisfies Text,
        to: '/programmes',
      },
      {
        image: '/assets/img/garage-repair-bench.jpg',
        date: { en: 'On member terms', mn: 'Гишүүний нөхцөлөөр' } satisfies Text,
        title: {
          en: 'Mentor a young person in your own trade',
          mn: 'Өөрийн мэргэжлээр залуу хүнийг чиглүүлээрэй',
        } satisfies Text,
        to: '/contact',
      },
    ],
  },
  contact: {
    titleKey: 'footer.contact' as MessageKey,
    links: [
      // The inbox is an address, not copy, and is printed as written in both languages.
      { icon: 'mail', label: 'garage@kkfmongolia.com', href: 'mailto:garage@kkfmongolia.com' },
      // No `href`: neither of these is something to open. The template printed
      // `123-456-7890` as a `tel:` link, which reads as a number somebody can ring.
      { icon: 'telephone-call', labelKey: 'footer.phone' as MessageKey },
      { icon: 'location', labelKey: 'footer.address' as MessageKey },
    ],
    newsletterPlaceholderKey: 'footer.newsletter' as MessageKey,
    agreementTextKey: 'footer.agreement' as MessageKey,
    privacyPolicy: { labelKey: 'legal.privacy' as MessageKey, to: '/privacy' },
  },
  copyrightNameKey: 'footer.copyright' as MessageKey,
  /*
   * The two links beside the copyright. Both pointed at `'#'` from the day the template
   * landed until 2026-09-19 — a link that scrolls to the top of the page you are on, on
   * every page of four sites. They are real routes now; the words are shared, the routes
   * are this app's. See `@kkf/design`'s `config/legal.ts`.
   */
  bottomNav: [
    { labelKey: 'legal.terms' as MessageKey, to: '/terms' },
    { labelKey: 'legal.privacy' as MessageKey, to: '/privacy' },
  ],
  vectorImage: '/assets/img/footer-vector-img.png',
}

/**
 * The off-canvas panel's footer.
 *
 * `sites` is the other three KKF sites, and **never this one** — see `MobileSidebar`'s
 * `sites` for why the list is composed here rather than derived in the design system.
 *
 * The same three places are in the footer's wayfinding column, and that is not a
 * duplication to collapse: the footer is several screens below the fold on a phone, and
 * the menu is what a phone visitor actually opens to go somewhere. One list of KKF sites
 * would mean the phone menu or the desktop footer losing its only route out.
 */
export const SIDEBAR = {
  followTitleKey: 'sidebar.follow' as MessageKey,
  socials: SOCIALS,
  sitesTitleKey: 'sidebar.sites' as MessageKey,
  sites: [
    { labelKey: 'nav.foundation' as MessageKey, href: LINKS.foundation },
    { labelKey: 'nav.resort' as MessageKey, href: LINKS.resort },
    { labelKey: 'nav.partners' as MessageKey, href: LINKS.partners },
  ],
}

/**
 * The bottom tab bar, below 992px. Every destination is a real route of this app and every
 * one is a **placeholder** — see the same note on the Foundation's. The filled tab is tours,
 * because booking one is the one thing this app completes end to end. It was the shop until
 * 2026-09-19, and the shop no longer exists.
 *
 * There is no house and no spanner in the icon font, so "home" is a star and "services" is a
 * package — a missing glyph renders nothing at all, not a fallback box.
 */
export const BOTTOM_NAV: { labelKey: MessageKey; icon: string; to: string; primary?: boolean }[] = [
  { labelKey: 'tab.home' as MessageKey, icon: 'star', to: '/' },
  { labelKey: 'tab.tours' as MessageKey, icon: 'location', to: '/tours', primary: true },
  { labelKey: 'tab.services' as MessageKey, icon: 'package', to: '/services' },
  { labelKey: 'tab.programmes' as MessageKey, icon: 'team', to: '/programmes' },
]
